/**
 * Boost cart drawer: tidy two labels the free tee offer adds.
 * 1) The Shopify "FREETEE" Buy X Get Y discount is listed at $0 on every item it counts toward the
 *    minimum, so Boost tags those lines with it. Hide a line's discount tag when that discount takes
 *    nothing off the line. A tag with a real amount (the tee itself, Boost bundles) stays.
 * 2) The tee is tagged boost:rewards:goal-exempt, so Boost notes "Doesn't count towards rewards" under
 *    it. Hide that note on the BOGOS gift row (marked data-wayfx-bogos-gift by the bridge).
 *
 * Loaded on theme pages by snippets/bogos-boost-bridge.liquid, and on the Boost collections page by
 * the Tracking Script through the data-wayfx-bogos-extra scripts in layout/bogos-embed.liquid.
 */
(() => {
  if (window.__wayfxBoostLabels) return;
  window.__wayfxBoostLabels = true;

  const ROOT_ID = 'eTVNlhWbns-app';
  const ROW = 'li.eTVNlhWbns__item';
  const TAG = '.eTVNlhWbns__item-discount';
  const ZERO = 'data-wayfx-zero-discount';

  const style = document.createElement('style');
  style.textContent = `
    #${ROOT_ID} ${TAG}[${ZERO}],
    #${ROOT_ID} .eTVNlhWbns__item[data-wayfx-bogos-gift] .eTVNlhWbns__item-message--muted { display: none !important; }
  `;
  document.head.append(style);

  // Line key -> titles of discounts that take $0 off that line, from the last cart read.
  let zeroTitles = new Map();
  let signature = '';
  let inFlight = false;
  let rerun = false;

  const tagTitle = (tag) => tag.textContent.trim();

  const apply = (root) => {
    root.querySelectorAll(ROW).forEach((row) => {
      const titles = zeroTitles.get(row.getAttribute('data-key'));
      row.querySelectorAll(TAG).forEach((tag) => {
        const zero = !!titles?.has(tagTitle(tag));
        if (zero !== tag.hasAttribute(ZERO)) tag.toggleAttribute(ZERO, zero);
      });
    });
  };

  // Read the cart only when the rows or their tags change; otherwise re-apply the last answer, in
  // case Boost re-rendered a tag.
  const sync = async (root) => {
    const rows = [...root.querySelectorAll(ROW)];
    const next = rows.map((row) => `${row.getAttribute('data-key')}=${[...row.querySelectorAll(TAG)].map(tagTitle).join(',')}`).join('|');
    if (next === signature || !next.includes('=') || !root.querySelector(TAG)) {
      apply(root);
      return;
    }
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = true;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return;
      const cart = await res.json();
      zeroTitles = new Map(
        cart.items.map((line) => [
          line.key,
          new Set(
            (line.line_level_discount_allocations ?? [])
              .filter((allocation) => Number(allocation.amount) === 0)
              .map((allocation) => allocation.discount_application?.title)
          ),
        ])
      );
      signature = next;
      apply(root);
    } catch (e) {
      // Leave the tags as Boost drew them; the next drawer change retries.
    } finally {
      inFlight = false;
      if (rerun) {
        rerun = false;
        schedule(root);
      }
    }
  };

  let timer;
  const schedule = (root) => {
    clearTimeout(timer);
    timer = setTimeout(() => sync(root), 150);
  };

  let bound = null;
  const bind = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root || root === bound) return !!root;
    bound = root;
    // Attribute writes above are not observed, so they never re-trigger this.
    new MutationObserver(() => schedule(root)).observe(root, { childList: true, subtree: true, characterData: true });
    schedule(root);
    return true;
  };

  const start = () => {
    if (bind()) return;
    const finder = new MutationObserver(() => bind() && finder.disconnect());
    finder.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => finder.disconnect(), 30000);
  };
  // Boost can mount its drawer after the watch window; a gift event rebinds then.
  document.addEventListener('fg-gifts:updated', bind);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
